import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;

const CustomerAuthContext = createContext(null);

export function CustomerAuthProvider({ children }) {
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processingAuth, setProcessingAuth] = useState(false);
  const processedRef = useRef(false);

  // Check if customer is logged in on mount
  useEffect(() => {
    checkAuth();
  }, []);

  // Handle Emergent Auth callback - check for session_id in URL fragment
  useEffect(() => {
    const handleAuthCallback = async () => {
      // Prevent double processing in StrictMode
      if (processedRef.current) return;
      
      const hash = window.location.hash;
      if (hash?.includes('session_id=')) {
        processedRef.current = true;
        setProcessingAuth(true);
        
        const sessionId = hash.split('session_id=')[1]?.split('&')[0];
        if (sessionId) {
          try {
            // Exchange session_id for customer data via backend
            const response = await fetch(`${API}/api/customer/auth/emergent-callback`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ session_id: sessionId }),
              credentials: 'include'
            });
            
            if (response.ok) {
              const data = await response.json();
              setCustomer(data.customer);
              // Clear the hash from URL
              window.history.replaceState(null, '', window.location.pathname + window.location.search);
            }
          } catch (error) {
            console.error('Emergent auth callback error:', error);
          }
        }
        setProcessingAuth(false);
      }
    };
    
    handleAuthCallback();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await fetch(`${API}/api/customer/auth/me`, {
        credentials: 'include'
      });
      const data = await response.json();
      setCustomer(data.customer);
    } catch (error) {
      console.error('Auth check failed:', error);
      setCustomer(null);
    } finally {
      setLoading(false);
    }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const loginWithGoogle = useCallback(() => {
    // Redirect to Emergent Auth - returns to current page with session_id
    const redirectUrl = window.location.origin + window.location.pathname;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);

  const guestCheckout = useCallback(async (name, email, phone) => {
    try {
      const response = await fetch(`${API}/api/customer/auth/guest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, phone }),
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Guest checkout failed');
      }
      
      const data = await response.json();
      setCustomer(data.customer);
      
      // Store session token in localStorage as backup
      if (data.session_token) {
        localStorage.setItem('customer_session', data.session_token);
      }
      
      return data.customer;
    } catch (error) {
      console.error('Guest checkout error:', error);
      throw error;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await fetch(`${API}/api/customer/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setCustomer(null);
      localStorage.removeItem('customer_session');
    }
  }, []);

  const getOrderHistory = useCallback(async () => {
    try {
      const response = await fetch(`${API}/api/customer/orders`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch order history');
      }
      
      const data = await response.json();
      return data.orders || [];
    } catch (error) {
      console.error('Order history error:', error);
      return [];
    }
  }, []);

  const updateSettings = useCallback(async (settings) => {
    try {
      const response = await fetch(`${API}/api/customer/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settings),
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to update settings');
      }
      
      // Refresh customer data
      await checkAuth();
      return true;
    } catch (error) {
      console.error('Update settings error:', error);
      return false;
    }
  }, []);

  const value = {
    customer,
    loading,
    processingAuth,
    isAuthenticated: !!customer,
    isGuest: customer?.auth_provider === 'guest',
    loginWithGoogle,
    guestCheckout,
    logout,
    getOrderHistory,
    updateSettings,
    checkAuth
  };

  return (
    <CustomerAuthContext.Provider value={value}>
      {children}
    </CustomerAuthContext.Provider>
  );
}

export function useCustomerAuth() {
  const context = useContext(CustomerAuthContext);
  if (!context) {
    throw new Error('useCustomerAuth must be used within a CustomerAuthProvider');
  }
  return context;
}

export default CustomerAuthContext;
